import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";

const AuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("rs_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const IDLE_TIMEOUT_MS = 15 * 60 * 1000;   // 15 min → force logout
const WARN_BEFORE_MS  =  5 * 60 * 1000;   // warn at 10 min idle (5 min before logout)
const ACTIVITY_EVENTS = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "click"];

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("rs_token"));
  const [loading, setLoading] = useState(true);
  const [sessionWarning, setSessionWarning] = useState(false);
  const [warnCountdown, setWarnCountdown] = useState(300);  // seconds remaining

  const idleTimer  = useRef(null);
  const warnTimer  = useRef(null);
  const countTimer = useRef(null);

  const logout = useCallback(() => {
    localStorage.removeItem("rs_token");
    setToken(null);
    setUser(null);
    setSessionWarning(false);
    clearTimeout(idleTimer.current);
    clearTimeout(warnTimer.current);
    clearInterval(countTimer.current);
  }, []);

  const resetIdleTimer = useCallback(() => {
    if (!localStorage.getItem("rs_token")) return;
    clearTimeout(idleTimer.current);
    clearTimeout(warnTimer.current);
    clearInterval(countTimer.current);
    setSessionWarning(false);

    warnTimer.current = setTimeout(() => {
      setSessionWarning(true);
      setWarnCountdown(WARN_BEFORE_MS / 1000);
      countTimer.current = setInterval(() => {
        setWarnCountdown(prev => {
          if (prev <= 1) { clearInterval(countTimer.current); return 0; }
          return prev - 1;
        });
      }, 1000);
    }, IDLE_TIMEOUT_MS - WARN_BEFORE_MS);

    idleTimer.current = setTimeout(() => {
      logout();
      window.location.href = "/login?reason=timeout";
    }, IDLE_TIMEOUT_MS);
  }, [logout]);

  // Attach/detach activity listeners when user is logged in
  useEffect(() => {
    if (!user) {
      clearTimeout(idleTimer.current);
      clearTimeout(warnTimer.current);
      clearInterval(countTimer.current);
      return;
    }
    resetIdleTimer();
    ACTIVITY_EVENTS.forEach(ev => window.addEventListener(ev, resetIdleTimer, { passive: true }));
    return () => {
      ACTIVITY_EVENTS.forEach(ev => window.removeEventListener(ev, resetIdleTimer));
      clearTimeout(idleTimer.current);
      clearTimeout(warnTimer.current);
      clearInterval(countTimer.current);
    };
  }, [user, resetIdleTimer]);

  useEffect(() => {
    const savedToken = localStorage.getItem("rs_token");
    if (savedToken) {
      api.get("/auth/me")
        .then((res) => setUser(res.data))
        .catch(() => { localStorage.removeItem("rs_token"); setToken(null); })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = (tokenVal, userData) => {
    localStorage.setItem("rs_token", tokenVal);
    setToken(tokenVal);
    setUser(userData);
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, loading, sessionWarning, warnCountdown, resetIdleTimer }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
